import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Lock, DollarSign, KeyRound, Copy, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReveal, setShowReveal] = useState(false);
  const [revealToken, setRevealToken] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [revealResult, setRevealResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Cookie auth — httpOnly cookies are set by the server, no token storage needed
      await axios.post(`${API}/admin/login`, { username, password });
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        // Brute-force lockout — surface the real reason (and wait time) instead
        // of a misleading "Invalid credentials".
        toast.error(detail || 'Too many attempts. Please wait a few minutes and try again.');
      } else if (!error.response) {
        toast.error('Cannot reach the server. Check your connection and try again.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Invalid credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = async () => {
    if (!revealToken.trim()) { toast.error('Paste the reveal token from your deploy logs'); return; }
    setRevealing(true);
    setRevealResult(null);
    try {
      const res = await axios.post(`${API}/admin/reveal-bootstrap-password`, { reveal_token: revealToken.trim() });
      setRevealResult(res.data);
      setUsername(res.data.username || 'admin');
      setPassword(res.data.password || '');
      toast.success('Password revealed — it will not be shown again.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reveal password');
    } finally {
      setRevealing(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(revealResult.password);
      setCopied(true);
      toast.success('Password copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper w-full max-w-md mx-auto px-4">
        <Card className="card-tech p-8" data-testid="admin-login-card">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold glow-text" data-testid="admin-login-title">ADMIN ACCESS</h1>
            <p className="text-green-400 mt-2">Millionaire The Machine Dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-green-400 text-sm font-semibold mb-2">Username</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-black/50 border-green-500/30 text-green-300 h-12"
                placeholder="Enter admin username"
                required
                data-testid="admin-username-input"
              />
            </div>
            <div>
              <label className="block text-green-400 text-sm font-semibold mb-2">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black/50 border-green-500/30 text-green-300 h-12"
                placeholder="Enter admin password"
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-6 text-lg"
              data-testid="admin-login-button"
            >
              {loading ? 'Authenticating...' : 'Access Dashboard'}
            </Button>
          </form>

          <div className="mt-6 p-3 bg-red-500/10 border border-red-500/30 rounded text-center">
            <p className="text-red-400 text-xs">
              Credentials stored securely in .env file. Never share or commit.
            </p>
          </div>

          <div className="mt-4 border-t border-green-500/20 pt-4">
            <button
              type="button"
              onClick={() => setShowReveal(v => !v)}
              className="w-full text-center text-green-400 text-sm underline hover:text-green-300"
              data-testid="reveal-toggle"
            >
              First-time setup? Reveal your auto-generated admin password
            </button>

            {showReveal && (
              <div className="mt-3 space-y-3" data-testid="reveal-section">
                <p className="text-green-500/70 text-xs">
                  If you deployed without setting <code className="text-green-400">ADMIN_PASSWORD</code>, a random one
                  was generated. Paste the one-time <strong>reveal token</strong> printed in your deploy logs to see it once.
                </p>
                <div className="flex items-stretch gap-2">
                  <Input
                    value={revealToken}
                    onChange={(e) => setRevealToken(e.target.value)}
                    placeholder="Reveal token from deploy logs"
                    className="bg-black/50 border-green-500/30 text-green-300 h-11"
                    data-testid="reveal-token-input"
                  />
                  <Button
                    type="button"
                    onClick={handleReveal}
                    disabled={revealing}
                    className="btn-secondary px-4 shrink-0"
                    data-testid="reveal-submit"
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> {revealing ? '...' : 'Reveal'}
                  </Button>
                </div>

                {revealResult && (
                  <div className="p-4 border-2 border-yellow-500/50 bg-yellow-500/10 rounded" data-testid="reveal-result">
                    <p className="text-yellow-400 font-bold text-xs mb-2">Save these now — the password won't be shown again:</p>
                    <p className="text-green-300 text-sm mb-1">Username: <code className="text-yellow-300">{revealResult.username}</code></p>
                    <div className="flex items-stretch gap-2 mt-1">
                      <code className="flex-1 text-yellow-300 font-mono bg-black/60 px-3 py-2 rounded break-all flex items-center" data-testid="reveal-password-value">{revealResult.password}</code>
                      <Button type="button" onClick={copyPassword} className="btn-secondary px-3 shrink-0" data-testid="reveal-copy" aria-label="Copy password">
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="text-center mt-4">
            <Button onClick={() => navigate('/')} variant="ghost" className="text-green-400 text-sm" data-testid="back-home-link">
              Back to Home
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
